/**
 * Prepare Initializer Component (V3 API only)
 *
 * For v3 API flow (JWT tokens), calls the prepare endpoint to get transaction context
 * (operation, lang, assetCode, transactionId, walletProvider) which replaces URL params.
 *
 * For v2/v5 API flow, this component does nothing as context comes from URL params.
 */

import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { useApiClientWithAutoVersion } from "../hooks/useApiClientWithAutoVersion";
import { rampsActions, useRampsStore } from "../stores/use-ramps-store";

export function PrepareInitializer() {
	const store = useRampsStore();
	const hasCalledPrepare = useRef(false);
	const navigate = useNavigate();
	const search = useSearch({ strict: false });

	// Use the same API client pattern as the rest of the app (handles proxy correctly)
	const { apiClient, apiVersion } = useApiClientWithAutoVersion({
		authToken: store.token,
	});

	// Determine if we should call prepare (v3 only)
	const token = store.token;

	// Note: Token doesn't change during a transaction, so we don't need to reset
	// hasCalledPrepare. It will persist for the entire transaction lifecycle.

	const shouldCallPrepare = Boolean(
		apiVersion === "v3" && token && !hasCalledPrepare.current,
	);

	// Log when prepare will be called (v3 flow)
	useEffect(() => {
		if (import.meta.env.DEV && apiVersion === "v3" && token) {
			console.log("🔧 V3 API detected - prepare endpoint will be called");
		}
	}, [apiVersion, token]);

	// Query for prepare endpoint (v3 only)
	const {
		data: prepareData,
		isSuccess,
		error,
	} = useQuery({
		queryKey: ["prepare", token, apiVersion],
		queryFn: async () => {
			if (import.meta.env.DEV) {
				console.log("🔄 Calling V3 prepare endpoint...");
			}
			return apiClient.transaction.prepare();
		},
		enabled: shouldCallPrepare,
		staleTime: Number.POSITIVE_INFINITY, // Never refetch - prepare is called once per session
		retry: 2,
	});

	// Store prepare response data
	useEffect(() => {
		if (isSuccess && prepareData && !hasCalledPrepare.current) {
			hasCalledPrepare.current = true;

			// Store prepare data in the Ramps store
			rampsActions.setPrepareData({
				operation: prepareData.operation,
				lang: prepareData.lang,
				assetCode: prepareData.assetCode,
				transactionId: prepareData.transactionId,
				walletProvider: prepareData.walletProvider,
			});

			// Route based on operation type
			if (prepareData.operation === "deposit") {
				// Pass current search params to maintain context (token, etc.)
				// Use defaults for required fields that may not be in current search
				navigate({
					to: "/ramps/deposit",
					search: {
						transaction_id: "",
						wallet_provider: "",
						asset_code: "USDC",
						operation: "deposit",
						lang: "en",
						amount: "",
						country: "",
						state: "",
						step: "deposit",
						...search,
					},
				});
			}

			if (import.meta.env.DEV) {
				console.log("✅ V3 Prepare response stored:", prepareData);
			}
		}
	}, [isSuccess, prepareData, navigate, search]);

	// Log errors in development
	useEffect(() => {
		if (error && import.meta.env.DEV) {
			console.error("❌ V3 Prepare endpoint error:", error);
		}
	}, [error]);

	// Log when we skip prepare (v2/v5 flow)
	useEffect(() => {
		if (token && import.meta.env.DEV) {
			if (apiVersion === "v2") {
				console.log(
					"ℹ️ V2 API flow - skipping prepare, using URL params for context",
				);
			} else if (apiVersion === "v5") {
				console.log(
					"ℹ️ V5 API flow - skipping prepare, using URL params for context",
				);
			}
		}
	}, [apiVersion, token]);

	// This is an initialization component - renders nothing
	return null;
}
